import { useState, useEffect, useContext } from "react";
import {
    Table,
    Typography,
    Spin,
    Tag,
    Button,
    message,
    Modal,
    Input,
    Descriptions,
    Tabs,
    Divider,
    DatePicker,
    Select,
    Row,
    Col,
} from "antd";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Mirrors ResignationRequest.Status choices on the backend
const STATUS_OPTIONS = [
    { value: "PENDING", label: "Pending Approval" },
    { value: "PENDING_HR_APPROVAL", label: "Pending HR Approval" },
    { value: "APPROVED", label: "Approved" },
    { value: "PENDING_FINAL_DATES", label: "Pending Final Dates" },
    { value: "COMPLETED", label: "Completed" },
    { value: "REJECTED", label: "Rejected" },
    { value: "WITHDRAWN", label: "Withdrawn" },
    { value: "PROCESS_HALTED", label: "Process Halted" },
];

export default function ResignationRequests() {
    const { authData, backUrl } = useContext(AuthContext);

    const [activeTab, setActiveTab] = useState("PENDING");
    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState(null); // 'resignation', 'withdrawal', 'waiver'

    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [finalizeModalVisible, setFinalizeModalVisible] = useState(false);
    const [lastWorkingDay, setLastWorkingDay] = useState(null);
    const [dateOfLeaving, setDateOfLeaving] = useState(null);

    // 🔹 Filters state
    const [filters, setFilters] = useState({
        name: "",
        resignedDateRange: [],
        lwdDateRange: [],
        status: "",
    });

    // 🔹 Fetch All Requests and Merge
    const fetchRequests = async (status = activeTab) => {
        try {
            setLoading(true);



            const headers = {
                Authorization: `Bearer ${authData?.access}`,
            };

            const [resignationsRes, withdrawalsRes, waiversRes] = await Promise.all([
                axios.get(
                    `${backUrl}/api/payroll/resignations/my-queue/`,
                    { headers }
                ),
                axios.get(
                    `${backUrl}/api/payroll/withdrawal-tasks/`,
                    { headers }
                ),
                axios.get(
                    `${backUrl}/api/payroll/waiver-tasks/`,
                    { headers }
                ),
            ]);

            const resignations =
                resignationsRes.data?.results ||
                resignationsRes.data ||
                [];

            const withdrawals =
                withdrawalsRes.data?.results ||
                withdrawalsRes.data ||
                [];

            const waivers =
                waiversRes.data?.results ||
                waiversRes.data ||
                [];

            const resignationMap = new Map(
                resignations.map((r) => [
                    r.id,
                    {
                        ...r,
                        withdrawal: null,
                        waiver: null,
                    },
                ])
            );

            withdrawals.forEach((withdrawal) => {
                const employeeId =
                    withdrawal.withdrawal_request?.employee?.id;

                const resignation = Array.from(
                    resignationMap.values()
                ).find(
                    (r) => r.employee?.id === employeeId
                );

                if (resignation) {
                    resignationMap.get(resignation.id).withdrawal =
                        withdrawal;
                }
            });

            waivers.forEach((waiver) => {
                const employeeId =
                    waiver.waiver_request?.employee?.id;

                const resignation = Array.from(
                    resignationMap.values()
                ).find(
                    (r) => r.request?.employee?.id === employeeId
                );

                if (resignation) {
                    resignationMap.get(resignation.id).waiver =
                        waiver;
                }
            });

            let mergedRequests = Array.from(resignationMap.values());

            if (activeTab) {
                mergedRequests = mergedRequests.filter(
                    (item) => getMyApprovalStatus(item) === activeTab
                );
            }

            setRequests(mergedRequests);
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests(activeTab);
    }, [activeTab]);

    // 🔹 Helper: resolve the resignation status whether `status` is a plain
    // string or the composite { resignation_status, waiver_status, withdrawal_status } object
    const getResignationStatus = (record) =>
        (record.status && typeof record.status === "object")
            ? record.status.resignation_status
            : record.status;

    // 🔹 Client-side filtering
    useEffect(() => {
        let result = [...requests];

        if (filters.name.trim()) {
            const q = filters.name.trim().toLowerCase();
            result = result.filter((r) =>
                (r.employee_name || "").toLowerCase().includes(q)
            );
        }

        // if (filters.status) {
        //     result = result.filter((r) => getResignationStatus(r) === filters.status);
        // }

        if (filters.resignedDateRange?.length === 2 && filters.resignedDateRange[0] && filters.resignedDateRange[1]) {
            const [start, end] = filters.resignedDateRange;
            result = result.filter((r) => {
                if (!r.resigned_date) return false;
                const d = dayjs(r.resigned_date);
                return (
                    d.isAfter(dayjs(start).subtract(1, "day")) &&
                    d.isBefore(dayjs(end).add(1, "day"))
                );
            });
        }

        if (filters.lwdDateRange?.length === 2 && filters.lwdDateRange[0] && filters.lwdDateRange[1]) {
            const [start, end] = filters.lwdDateRange;
            result = result.filter((r) => {
                if (!r.requested_last_working_day) return false;
                const d = dayjs(r.requested_last_working_day);
                return (
                    d.isAfter(dayjs(start).subtract(1, "day")) &&
                    d.isBefore(dayjs(end).add(1, "day"))
                );
            });
        }

        setFilteredRequests(result);
    }, [filters, requests]);

    const getMyApprovalStatus = (record) => {
        const myLog = record.approval_logs?.find((log) =>
            log.approver_name
                ?.toLowerCase()
                .includes(authData?.employee?.first_name?.toLowerCase())
        );

        return myLog?.status || "PENDING";
    };

    const handleClearFilters = () => {
        setFilters({
            name: "",
            resignedDateRange: [],
            lwdDateRange: [],
        });
    };

    // 🔹 Open Approve Modal
    const openApproveModal = (record, type) => {
        setSelectedRequest(record);
        setActionType(type);
        setRemarks("");
        setModalVisible(true);
    };

    // 🔹 Handle Approval
    const handleApprove = async () => {
        if (!remarks.trim()) {
            toast.warning("Please enter remarks");
            return;
        }

        try {
            setApproving(true);
            let endpoint = "";

            if (actionType === "resignation") {
                endpoint = `${backUrl}/api/payroll/resignations/${selectedRequest.id}/approve/`;
            } else if (actionType === "withdrawal") {
                endpoint = `${backUrl}/api/payroll/withdrawal-requests/${selectedRequest.withdrawal.withdrawal_request.id}/approve/`;
            } else if (actionType === "waiver") {
                endpoint = `${backUrl}/api/payroll/waiver-requests/${selectedRequest.waiver.waiver_request.id}/approve/`;
            }

            await axios.post(
                endpoint,
                { action: "APPROVED", remarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} approved successfully`);
            setModalVisible(false);
            fetchRequests();
        } catch (err) {
            console.error(err);
            toast.error("Approval failed");
        } finally {
            setApproving(false);
        }
    };

    // 🔹 Open Finalize Modal
    const openFinalizeModal = (record) => {
        setSelectedRequest(record);
        setLastWorkingDay(record.requested_last_working_day ? dayjs(record.requested_last_working_day) : null);
        setDateOfLeaving(record.requested_last_working_day ? dayjs(record.requested_last_working_day) : null);
        setFinalizeModalVisible(true);
    };

    // 🔹 Handle Finalize
    const handleFinalize = async () => {
        if (!lastWorkingDay || !dateOfLeaving) {
            toast.warning("Please select both Last Working Day and Date of Leaving");
            return;
        }
        if (dateOfLeaving.isBefore(lastWorkingDay)) {
            toast.warning("Date of leaving cannot be before the last working day");
            return;
        }

        try {
            setApproving(true);
            const payload = {
                last_working_day: lastWorkingDay.format("YYYY-MM-DD"),
                date_of_leaving: dateOfLeaving.format("YYYY-MM-DD"),
            };

            await axios.post(
                `${backUrl}/api/payroll/resignations/${selectedRequest.id}/hr-finalize/`,
                payload,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success("Resignation dates finalized successfully");
            setFinalizeModalVisible(false);
            fetchRequests();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || err.response?.data?.non_field_errors?.[0] || "Finalization failed");
        } finally {
            setApproving(false);
        }
    };

    // 🔹 Open Details Modal
    const openDetailsModal = (record) => {
        setSelectedDetails(record);
        setDetailsModal(true);
    };

    // 🔹 Columns
    const isSuperuser = authData?.employee?.is_superuser;

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            render: (name, record) => (
                <Button type="link" onClick={() => openDetailsModal(record)}>
                    {name}
                </Button>
            ),
        },
        {
            title: "Resigned Date",
            dataIndex: "resigned_date",
            render: (d) => d ? new Date(d).toLocaleDateString("en-GB") : "-",
        },
        {
            title: "Requested LWD",
            dataIndex: "requested_last_working_day",
            render: (d) => d ? new Date(d).toLocaleDateString("en-GB") : "-",
        },
        !isSuperuser && {
            title: "Resignation Status",
            dataIndex: "my_status",
            render: (s) => (
                <Tag color={s === "APPROVED" ? "green" : "orange"}>{s}</Tag>
            ),
        },
        {
            title: "Withdrawal",
            render: (_, record) => {
                if (!record.withdrawal) return <Tag>-</Tag>;
                const status = record.withdrawal.status;
                return (
                    <Tag color={status === "APPROVED" ? "green" : status === "PENDING" ? "orange" : "red"}>
                        {status}
                    </Tag>
                );
            },
        },
        {
            title: "Waiver",
            render: (_, record) => {
                if (!record.waiver) return <Tag>-</Tag>;
                const status = record.waiver.status;
                return (
                    <>
                        <Tag color={status === "APPROVED" ? "green" : status === "PENDING" ? "orange" : "red"}>
                            {status}
                        </Tag>
                        <p style={{ color: 'black' }}>{record?.waiver?.request?.reason}</p>
                    </>

                );
            },
        },
        {
            title: "Overall Status",
            dataIndex: "status",
            render: (s) => {
                // `status` can come back as a composite object
                // ({ resignation_status, waiver_status, withdrawal_status })
                // rather than a plain string — render each part as its own tag
                // instead of trying to print the object directly (React error #31).
                if (s && typeof s === "object") {
                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {s.resignation_status && (
                                <Tag color={s.resignation_status === "APPROVED" ? "green" : "orange"}>
                                    Resignation: {s.resignation_status}
                                </Tag>
                            )}
                            {s.withdrawal_status && (
                                <Tag color={s.withdrawal_status === "APPROVED" ? "green" : s.withdrawal_status === "PENDING" ? "orange" : "red"}>
                                    Withdrawal: {s.withdrawal_status}
                                </Tag>
                            )}
                            {s.waiver_status && (
                                <Tag color={s.waiver_status === "APPROVED" ? "green" : s.waiver_status === "PENDING" ? "orange" : "red"}>
                                    Waiver: {s.waiver_status}
                                </Tag>
                            )}
                        </div>
                    );
                }
                return <Tag color={s === "APPROVED" ? "green" : "orange"}>{s}</Tag>;
            },
        },
        {
            title: "Actions",
            render: (_, record) => {
                const isHrOrSuper = authData?.employee?.is_hr || authData?.employee?.is_superuser;
                const resignationStatus = getResignationStatus(record);
                return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {/* Resignation Approval */}
                        {resignationStatus !== "PENDING_FINAL_DATES" && resignationStatus !== "COMPLETED" && resignationStatus !== "REJECTED" && resignationStatus !== "WITHDRAWN" && (
                            <Button
                                type="primary"
                                size="small"
                                disabled={record.my_status === "APPROVED"}
                                onClick={() => openApproveModal(record, "resignation")}
                                style={{ background: '#0e497b', color: 'white' }}
                            >
                                Approve Resignation
                            </Button>
                        )}

                        {/* Finalize Dates */}
                        {resignationStatus === "PENDING_FINAL_DATES" && isHrOrSuper && (
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => openFinalizeModal(record)}
                                style={{ background: '#28a745', color: 'white', borderColor: '#28a745' }}
                            >
                                Finalize Dates
                            </Button>
                        )}

                        {/* Withdrawal Approval */}
                        {record.withdrawal && (
                            <Button
                                type="primary"
                                size="small"
                                disabled={record.withdrawal.status === "APPROVED"}
                                onClick={() => openApproveModal(record, "withdrawal")}
                                style={{ background: '#0e497b', color: 'white' }}
                            >
                                Approve Withdrawal
                            </Button>
                        )}

                        {/* Waiver Approval */}
                        {record.waiver && (
                            <Button
                                type="primary"
                                size="small"
                                disabled={record.waiver.status === "APPROVED"}
                                onClick={() => openApproveModal(record, "waiver")}
                                style={{ background: '#0e497b', color: 'white' }}
                            >
                                Approve Waiver
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ].filter(Boolean);

    return (
        <div style={{ maxWidth: "95%", margin: "40px auto" }}>
            <Title level={4}>Resignation Requests</Title>

            <div style={{ marginBottom: 20, textAlign: "center" }}>
                <Button
                    type={activeTab === "PENDING" ? "primary" : "default"}
                    onClick={() => setActiveTab("PENDING")}
                    style={{ marginRight: 30 }}
                >
                    Pending
                </Button>

                <Button
                    type={activeTab === "APPROVED" ? "primary" : "default"}
                    onClick={() => setActiveTab("APPROVED")}
                    style={{ marginRight: 30 }}
                >
                    Approved
                </Button>

                <Button
                    type={activeTab === "REJECTED" ? "primary" : "default"}
                    onClick={() => setActiveTab("REJECTED")}
                    style={{ marginRight: 30 }}
                >
                    Rejected
                </Button>
            </div>

            {/* 🔹 Filter Section */}
            <div
                style={{
                    background: "#ffffff",
                    padding: "15px",
                    borderRadius: "10px",
                    marginBottom: "15px",
                    border: "1px solid #e8e8e8",
                }}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Employee Name"
                            value={filters.name}
                            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                            allowClear
                        />
                    </Col>
                    {/* <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Status"
                            value={filters.status || undefined}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                            allowClear
                            style={{ width: "100%" }}
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                    </Col> */}
                    <Col xs={24} sm={12} md={6}>
                        <RangePicker
                            style={{ width: "100%" }}
                            placeholder={["Resigned From", "Resigned To"]}
                            format="DD/MM/YYYY"
                            value={filters.resignedDateRange}
                            onChange={(dates) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    resignedDateRange: dates ? [...dates] : [],
                                }))
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <RangePicker
                            style={{ width: "100%" }}
                            placeholder={["LWD From", "LWD To"]}
                            format="DD/MM/YYYY"
                            value={filters.lwdDateRange}
                            onChange={(dates) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    lwdDateRange: dates ? [...dates] : [],
                                }))
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Button block onClick={handleClearFilters}>
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </div>

            {loading ? (
                <Spin size="large" style={{ display: "block", margin: "40px auto" }} />
            ) : (
                <Table
                    dataSource={filteredRequests}
                    columns={columns}
                    rowKey="id"
                    bordered
                    pagination={{ pageSize: 15 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: 'white' }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            {/* 🔹 Approve Modal */}
            <Modal
                title={`Approve ${actionType ? actionType.charAt(0).toUpperCase() + actionType.slice(1) : 'Request'}`}
                open={modalVisible}
                onOk={handleApprove}
                onCancel={() => setModalVisible(false)}
                confirmLoading={approving}
            >
                <Text strong>Remarks</Text>
                <TextArea
                    rows={4}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                />
            </Modal>

            {/* 🔹 Finalize Dates Modal */}
            <Modal
                title="Finalize Exit Dates"
                open={finalizeModalVisible}
                onOk={handleFinalize}
                onCancel={() => setFinalizeModalVisible(false)}
                confirmLoading={approving}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "15px", padding: "10px 0" }}>
                    <div>
                        <div style={{ marginBottom: "5px" }}><Text strong>Last Working Day</Text></div>
                        <DatePicker
                            style={{ width: "100%" }}
                            value={lastWorkingDay}
                            onChange={(date) => setLastWorkingDay(date)}
                            format="YYYY-MM-DD"
                        />
                    </div>
                    <div>
                        <div style={{ marginBottom: "5px" }}><Text strong>Date of Leaving</Text></div>
                        <DatePicker
                            style={{ width: "100%" }}
                            value={dateOfLeaving}
                            onChange={(date) => setDateOfLeaving(date)}
                            format="YYYY-MM-DD"
                        />
                    </div>
                </div>
            </Modal>

            {/* 🔹 Details Modal with Tabs */}
            <Modal
                title="Request Details"
                open={detailsModal}
                onCancel={() => setDetailsModal(false)}
                footer={null}
                width={900}
            >
                {selectedDetails && (
                    <Tabs
                        defaultActiveKey="resignation"
                        items={[
                            {
                                key: "resignation",
                                label: "Resignation",
                                children: (
                                    <>
                                        <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
                                            <Descriptions.Item label="Employee">
                                                {selectedDetails.employee_name}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Resigned Date">
                                                {selectedDetails.resigned_date}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Requested LWD">
                                                {selectedDetails.requested_last_working_day}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Pending With">
                                                {selectedDetails.pending_with || "-"}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Divider />
                                        <h6>Reason</h6>
                                        <br />
                                        <Paragraph style={{ fontSize: "18px", lineHeight: 1.8, whiteSpace: "pre-line", marginBottom: 0 }}>
                                            {selectedDetails.reason}
                                        </Paragraph>
                                        <br />
                                        <Divider />

                                        <Title level={5}>Approval Logs</Title>
                                        <Table
                                            dataSource={selectedDetails.approval_logs || []}
                                            rowKey={(row, i) => i}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            columns={[
                                                { title: "Order", dataIndex: "order" },
                                                { title: "Approver", dataIndex: "approver_name" },
                                                {
                                                    title: "Status",
                                                    dataIndex: "status",
                                                    render: (s) => (
                                                        <Tag color={s === "APPROVED" ? "green" : s === "PENDING" ? "orange" : "red"}>
                                                            {s}
                                                        </Tag>
                                                    ),
                                                },
                                                { title: "Remarks", dataIndex: "remarks", render: (r) => r || "-" },
                                                {
                                                    title: "Action Time",
                                                    dataIndex: "action_timestamp",
                                                    render: (t) => t ? new Date(t).toLocaleString("en-GB") : "-",
                                                },
                                            ]}
                                        />
                                    </>
                                ),
                            },
                            selectedDetails.withdrawal && {
                                key: "withdrawal",
                                label: "Withdrawal",
                                children: (
                                    <>
                                        <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
                                            <Descriptions.Item label="Employee">
                                                {selectedDetails.withdrawal.withdrawal_request?.employee?.user?.first_name || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Reason">
                                                {selectedDetails.withdrawal.withdrawal_request?.reason || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Status">
                                                <Tag color={selectedDetails.withdrawal.status === "APPROVED" ? "green" : "orange"}>
                                                    {selectedDetails.withdrawal.status}
                                                </Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created At">
                                                {selectedDetails.withdrawal.withdrawal_request?.created_at
                                                    ? new Date(selectedDetails.withdrawal.withdrawal_request.created_at).toLocaleString("en-GB")
                                                    : "-"}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Title level={5}>Approval Logs</Title>
                                        <Table
                                            dataSource={selectedDetails.withdrawal.withdrawal_request?.approval_logs || []}
                                            rowKey={(row, i) => i}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            columns={[
                                                { title: "Order", dataIndex: "order" },
                                                { title: "Approver", dataIndex: "approver_name" },
                                                {
                                                    title: "Status",
                                                    dataIndex: "status",
                                                    render: (s) => (
                                                        <Tag color={s === "APPROVED" ? "green" : s === "PENDING" ? "orange" : "red"}>
                                                            {s}
                                                        </Tag>
                                                    ),
                                                },
                                                { title: "Remarks", dataIndex: "remarks", render: (r) => r || "-" },
                                                {
                                                    title: "Action Time",
                                                    dataIndex: "action_timestamp",
                                                    render: (t) => t ? new Date(t).toLocaleString("en-GB") : "-",
                                                },
                                            ]}
                                        />
                                    </>
                                ),
                            },
                            selectedDetails.waiver && {
                                key: "waiver",
                                label: "Waiver",
                                children: (
                                    <>
                                        <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
                                            <Descriptions.Item label="Employee">
                                                {selectedDetails.waiver.waiver_request?.employee?.user?.first_name || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Reason">
                                                {selectedDetails.waiver.waiver_request?.reason || "-"}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Status">
                                                <Tag color={selectedDetails.waiver.status === "APPROVED" ? "green" : "orange"}>
                                                    {selectedDetails.waiver.status}
                                                </Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created At">
                                                {selectedDetails.waiver.waiver_request?.created_at
                                                    ? new Date(selectedDetails.waiver.waiver_request.created_at).toLocaleString("en-GB")
                                                    : "-"}
                                            </Descriptions.Item>
                                        </Descriptions>




                                        <Title level={5}>Approval Logs</Title>
                                        <Table
                                            dataSource={selectedDetails.waiver.waiver_request?.approval_logs || []}
                                            rowKey={(row, i) => i}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            columns={[
                                                { title: "Order", dataIndex: "order" },
                                                { title: "Approver", dataIndex: "approver_name" },
                                                {
                                                    title: "Status",
                                                    dataIndex: "status",
                                                    render: (s) => (
                                                        <Tag color={s === "APPROVED" ? "green" : s === "PENDING" ? "orange" : "red"}>
                                                            {s}
                                                        </Tag>
                                                    ),
                                                },
                                                { title: "Remarks", dataIndex: "remarks", render: (r) => r || "-" },
                                                {
                                                    title: "Action Time",
                                                    dataIndex: "action_timestamp",
                                                    render: (t) => t ? new Date(t).toLocaleString("en-GB") : "-",
                                                },
                                            ]}
                                        />
                                    </>
                                ),
                            },
                        ].filter(Boolean)}
                    />
                )}
            </Modal>

            <ToastContainer />
        </div>
    );
}